import React, { useEffect, useState } from "react";
import LinearProgress from "@carrier-io/air-react/LinearProgress";
import Grid from "@carrier-io/air-react/Grid";
import Typography from "@carrier-io/air-react/Typography";
import Alert from "@carrier-io/air-react/Alert";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthenticationResponse } from "../../generated/graphql";
import { RoutesPage } from "../../constants/routes";
import { LoginCredentialsMutation } from "../../graphql/mutations/loginCredentials";
import { ForgotPasswordMutation } from "../../graphql/mutations/forgotPassword";
import { Clients, useMutation } from "../../apollo";
import {
  clearLoginAttempts,
  deleteLoginCookies,
  deleteLookupsUserCookies,
  increaseLoginAttempts,
  MAXIMUM_LOGIN_ATTEMPTS,
  setLoginCookies,
} from "../../utils/cookie";
import { AWSRegions } from "../../generated/admin/graphql";
import { useForm } from "react-hook-form";
import { SubmitButton } from "../../components/submitButton/SubmitButton";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { PasswordInput } from "../../validations/password";
import { ControllablePasswordInput } from "../../components/controllable/PasswordInput";
import Box from "@carrier-io/air-react/Box";
import Button from "@carrier-io/air-react/Button";
import { AlertTypes } from "../../contexts/alert/types";
import { useAlerts } from "../../contexts/alert/AlertsContext";
import { EmailChangeAccount } from "../../components/emailChangeAccount/emailChangeAccount";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

enum PasswordResetStatus {
  IDLE = "idle",
  SENDING = "sending",
  SENT = "sent",
  ERROR = "error",
}

export const PasswordLoginPage = () => {
  const [searchParams] = useSearchParams();

  const { pushAlert } = useAlerts();
  const resolver = classValidatorResolver<PasswordInput>(PasswordInput);
  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<PasswordInput>({
    mode: "onBlur",
    resolver,
    defaultValues: {
      password: "",
    },
  });
  const redirectTo = searchParams.get("redirect_to");
  const email = searchParams.get("email");
  const username = searchParams.get("username");
  const region = searchParams.get("region");
  const prevError = searchParams.get("prevError");

  const [passwordResetStatus, setPasswordResetStatus] =
    useState<PasswordResetStatus>(PasswordResetStatus.IDLE);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!username || !email || !region) {
      deleteLoginCookies(window.location.hostname);
      navigate(RoutesPage.Login);
    }
  }, [navigate, username, email, region]);

  const [runCredLogin, credLoginResult] = useMutation<
    { authCredentials: AuthenticationResponse },
    { username: string; password: string; region: AWSRegions }
  >(Clients.Authn, LoginCredentialsMutation);

  const [runForgotPassword, forgotPasswordResult] = useMutation<
    { forgotPassword: AuthenticationResponse },
    { username: string; email: string; region: AWSRegions; siteUrl: string }
  >(Clients.Authn, ForgotPasswordMutation);

  const handleSubmitPassword = async (data: PasswordInput) => {
    const { password } = data;
    try {
      const credLoginResult = await runCredLogin({
        variables: {
          username: username ?? "",
          password: password ?? "",
          region: region as AWSRegions,
        },
      });

      if (!credLoginResult.data) {
        setError("Failed to login. Please try again.");
        return;
      }
      const { idToken, accessToken, refreshToken } =
        credLoginResult.data.authCredentials;

      setLoginCookies(
        { idToken, accessToken, refreshToken },
        window.location.hostname,
      );
      clearLoginAttempts(window.location.hostname);
      navigate({
        pathname: RoutesPage.ConsentModal,
        search: searchParams.toString(),
      });
      return;
    } catch (error) {
      setPasswordResetStatus(PasswordResetStatus.ERROR);
      setError((error as Error).message);
      setValue("password", "", {
        shouldValidate: true,
      });
    }

    const currentAttempts = increaseLoginAttempts(window.location.hostname);
    if (currentAttempts > MAXIMUM_LOGIN_ATTEMPTS) {
      deleteLookupsUserCookies(window.location.hostname);
      pushAlert(AlertTypes.ERROR, "Attempt limit exceeded");
      navigate({
        pathname: RoutesPage.Login,
        search: `?redirect_to=${redirectTo}`,
      });
    }
  };

  const handleForgotPassword = async (shouldReset: boolean) => {
    if (!shouldReset) {
      setPasswordResetStatus(PasswordResetStatus.IDLE);
      return;
    }

    try {
      setPasswordResetStatus(PasswordResetStatus.SENDING);
      const siteUrl = window.location.origin + RoutesPage.ForgotPassword;

      const forgotPasswordResult = await runForgotPassword({
        variables: {
          username: username ?? "",
          email: email ?? "",
          region: region as AWSRegions,
          siteUrl,
        },
      });

      if (!forgotPasswordResult.data) {
        setPasswordResetStatus(PasswordResetStatus.ERROR);
        setError("Attempt limit exceeded, please try in an hour.");
        return;
      }

      setPasswordResetStatus(PasswordResetStatus.SENT);
    } catch {
      setPasswordResetStatus(PasswordResetStatus.ERROR);
      setError("Attempt limit exceeded, please try in an hour.");
    } finally {
      setValue("password", "", {
        shouldValidate: true,
      });
    }
  };

  const handleForgotPasswordClick = () => {
    handleForgotPassword(true);
    clearErrors("password");
  };

  return (
    <>
      <Grid marginTop="10px">
        <Typography variant="body2" padding="0">
          Sign in with your email address
        </Typography>
      </Grid>
      {prevError && (
        <Alert
          severity="error"
          icon={
            <WarningAmberIcon
              sx={{
                alignSelf: "center",
              }}
            />
          }
          sx={{
            backgroundColor: "#fff6f6",
            color: "black",
            borderRadius: "8px",
            boxShadow: "0px 3px 5px 0px rgba(20, 22, 24, 0.2)",
            maxWidth: 500,
            display: "flex",
            alignItems: "center",
          }}
        >
          {prevError}
        </Alert>
      )}
      <Box marginTop="20px" width="100%">
        <EmailChangeAccount email={email ?? ""} />
      </Box>
      <form
        style={{ width: "100%" }}
        onSubmit={handleSubmit(handleSubmitPassword)}
      >
        <Box height="66px">
          {passwordResetStatus === PasswordResetStatus.SENT && (
            <Alert
              onClose={() => handleForgotPassword(false)}
              role="alert"
              severity="success"
              variant="outlined"
            >
              Password reset instructions were sent to your email.
            </Alert>
          )}
          {passwordResetStatus === PasswordResetStatus.ERROR && error && (
            <Alert
              onClose={() => {
                setError("");
                handleForgotPassword(false);
              }}
              role="alert"
              severity="error"
              variant="outlined"
            >
              {error}
            </Alert>
          )}
          {[PasswordResetStatus.IDLE, PasswordResetStatus.SENDING].includes(
            passwordResetStatus,
          ) && (
            <Box maxWidth="338px" width="100%" margin="0 auto">
              <ControllablePasswordInput
                control={control}
                name="password"
                error={errors.password?.message}
                color="primary"
                label="Password"
                size="large"
                hideBackgroundColor
                fullWidth
                showBorder
                type="password"
                data-testId="password"
              />
            </Box>
          )}
        </Box>
        <Box alignSelf="flex-start" padding="8px 0" height="20px">
          {passwordResetStatus === PasswordResetStatus.SENDING && (
            <LinearProgress />
          )}
          {passwordResetStatus === PasswordResetStatus.IDLE && (
            <Button
              onClick={handleForgotPasswordClick}
              data-testid="forgot-password"
              variant="text"
              disabled={forgotPasswordResult.loading}
              sx={{
                height: "initial !important",
                padding: 0,
                fontWeight: "400 !important",
                background: "transparent !important",
              }}
            >
              Forgot your password?
            </Button>
          )}
        </Box>
        <Box maxWidth="338px" width="100%" margin="20px auto 0">
          <SubmitButton
            isLoading={credLoginResult.loading}
            disabled={!isValid || credLoginResult.loading}
          >
            Next
          </SubmitButton>
        </Box>
      </form>
    </>
  );
};
